import type {
  NotificationProps,
  NotificationOptions,
  NotifyMessageCardFields,
  NotificationColor,
  NotificationConfig,
} from '@/shared/types/message';
import type { SizeAlert } from '@/shared/types/alert';

/**
 * Construye la notificación persistida al encolar desde opciones ya separadas de metadatos de API.
 *
 * @param id - Identificador único asignado por el servicio.
 * @param finalOptions - Opciones de `Notification` más título/mensaje ya normalizados.
 * @param color - Color de cola interno (`brand` / `neutral`) derivado del mazo.
 * @param notificationCard - Overrides tipados de tarjeta (`NotifyMessageCardFields`) o `undefined`.
 * @param config - Snapshot de `NotificationConfig` (duración, tamaño, timer, posición, …).
 * @returns Modelo `NotificationProps` listo para la cola del grupo.
 */
export function buildNotificationPropsForEnqueue(
  id: number,
  finalOptions: NotificationOptions & { title?: string },
  color: NotificationColor,
  notificationCard: NotifyMessageCardFields | undefined,
  config: NotificationConfig
): NotificationProps {
  const message = finalOptions.message ?? '';
  const title = finalOptions.title;

  const notification: NotificationProps = {
    id,
    message,
    title,
    subtitle: finalOptions.subtitle,
    description: finalOptions.description,
    color,
    avatar: finalOptions.avatar,
    icon: finalOptions.icon,
    position: finalOptions.position ?? config.position,
    duration: finalOptions.duration ?? config.duration,
    showCloseButton: finalOptions.showCloseButton ?? true,
    isLeaving: false,
    showTimer: config.showTimer,
    onClick: finalOptions.onClick ?? notificationCard?.events?.onClick,
    onClose: finalOptions.onClose,
    onPrimaryClick: finalOptions.onPrimaryClick,
    onSecondaryClick: finalOptions.onSecondaryClick,
    primaryButtonText: finalOptions.primaryButtonText,
    secondaryButtonText: finalOptions.secondaryButtonText,
    size: (finalOptions.size ?? (config.size === 'lg' ? 'md' : config.size)) as SizeAlert,
    variant: finalOptions.variant ?? 'outlined',
    animation: finalOptions.animation ?? 'fade',
    notificationCard,
  };

  applyNotificationEnqueueDevFixes(notification, config);
  return notification;
}

function applyNotificationEnqueueDevFixes(
  notification: NotificationProps,
  config: NotificationConfig
): void {
  if (process.env.NODE_ENV === 'production') return;

  if (
    notification.duration !== undefined &&
    (isNaN(notification.duration) || !isFinite(notification.duration) || notification.duration < 0)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[NotificationService] Invalid duration "${notification.duration}". Using default.`
    );
    notification.duration = config.duration;
  }
  if (
    notification.position &&
    ![
      'top-left',
      'top-center',
      'top-right',
      'center-left',
      'center',
      'center-right',
      'bottom-left',
      'bottom-center',
      'bottom-right',
    ].includes(notification.position)
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[NotificationService] Invalid position "${notification.position}". Using default.`
    );
    notification.position = config.position;
  }
}
